import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { Stack, Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { SimpleIssueCard } from '../../../../../common/ui/simple-issue-card/index.tsx';
import type { body_versionIssueAssociatedDesigns_IssuesModalBody$key } from './__generated__/body_versionIssueAssociatedDesigns_IssuesModalBody.graphql';
import messages from './messages.tsx';
import { useFireManyIssueTrackingAnalaytics } from './utils.tsx';

type Props = {
	devOpsDesign: body_versionIssueAssociatedDesigns_IssuesModalBody$key;
};
export const IssuesModalBody = ({ devOpsDesign }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const designEntity = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment body_versionIssueAssociatedDesigns_IssuesModalBody on DevOpsDesign {
				associatedIssues(first: 100) {
					edges {
						node {
							... on JiraIssue {
								key @required(action: THROW)
								hasRelationshipToVersion(versionId: $versionId)
								...simpleIssueCard_versionIssueAssociatedDesigns
							}
						}
					}
				}
			}
		`,
		devOpsDesign,
	);

	const associatedIssuesNodes = (designEntity?.associatedIssues?.edges ?? [])
		.filter(Boolean)
		.map((edge) => edge.node)
		.filter(Boolean);
	const issuesAssociatedToThisVersion = associatedIssuesNodes.filter(
		(node) => node.hasRelationshipToVersion,
	);
	const issuesNotAssociatedToThisVersion = associatedIssuesNodes.filter(
		(node) => !node.hasRelationshipToVersion,
	);

	useFireManyIssueTrackingAnalaytics(associatedIssuesNodes.length);

	return (
		<ContextualAnalyticsData
			sourceName="versionDesignAssociatedIssues"
			sourceType={SCREEN}
			attributes={{
				issuesAssociatedTotalCount: associatedIssuesNodes.length,
				issuesAssociatedToVersionCount: issuesAssociatedToThisVersion.length,
				issuesNotAssociatedToVersionCount: issuesAssociatedToThisVersion.length,
			}}
		>
			<Stack space="space.300" xcss={modalBodyStyles}>
				<Stack>
					{issuesAssociatedToThisVersion.length > 0 && (
						<Heading size="xsmall">
							<Inline xcss={subTitleStyles}>
								{formatMessage(messages.issuesInVersion)}
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<Inline xcss={issueCountStyles}>
									&nbsp; ({/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									{issuesAssociatedToThisVersion.length})
								</Inline>
							</Inline>
						</Heading>
					)}
					{issuesAssociatedToThisVersion.map((issueNode) => (
						<SimpleIssueCard key={issueNode.key} jiraIssue={issueNode} />
					))}
				</Stack>
				<Stack>
					{issuesNotAssociatedToThisVersion.length > 0 && (
						<Heading size="xsmall">
							<Inline xcss={subTitleStyles}>
								{formatMessage(messages.otherIssues)}
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<Inline xcss={issueCountStyles}>
									&nbsp; ({/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									{issuesNotAssociatedToThisVersion.length})
								</Inline>
							</Inline>
						</Heading>
					)}
					{issuesNotAssociatedToThisVersion.map((issueNode) => (
						<SimpleIssueCard key={issueNode.key} jiraIssue={issueNode} />
					))}
				</Stack>
			</Stack>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const modalBodyStyles = xcss({
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
});

const subTitleStyles = xcss({
	marginBottom: 'space.100',
});

const issueCountStyles = xcss({
	color: 'color.text.subtlest',
	fontWeight: token('font.weight.regular'),
});
